import { useState } from 'react';
import cn from 'classnames';
// Ui
import Text from 'ui3/Text/Text';
import Button from 'ui3/Button/Button';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Components
import CompetitorSearchInput, {
  CompetitorSearchResult,
} from '../CompetitorSearchInput/CompetitorSearchInput';
import InviteParticipantModal, {
  InviteParticipantResult,
} from '../InviteParticipantModal/InviteParticipantModal';
import { CompetitorDraft, isValidInviteEmail } from '../types';
// Styles
import styles from './CompetitorSlot.module.scss';

const UNPICKED_ERROR = 'Not added yet. Pick from the results or use Invite.';

type CompetitorSlotProps = {
  corner: 'A' | 'B';
  competitor: CompetitorDraft;
  onChange: (competitor: CompetitorDraft) => void;
  excludeStoreIds?: string[];
  searchTerm?: string;
  onSearchTermChange?: (searchTerm: string) => void;
};

const CompetitorSlot = ({
  corner,
  competitor,
  onChange,
  excludeStoreIds,
  searchTerm,
  onSearchTermChange,
}: CompetitorSlotProps): JSX.Element => {
  const [showInviteModal, setShowInviteModal] = useState(false);

  const handleSearchSelect = (result: CompetitorSearchResult) => {
    onChange(result);
  };

  const handleInvite = (invitee: InviteParticipantResult) => {
    onChange(invitee);
  };

  const handleOpenInviteModal = () => {
    setShowInviteModal(true);
  };

  const handleCloseInviteModal = () => {
    setShowInviteModal(false);
  };

  const handleRemoveCompetitor = () => {
    onChange(null);
  };

  const isRegistered = Boolean(competitor?.storeId);
  const isInvitee = Boolean(competitor) && !isRegistered;
  const hasInvalidEmail =
    isInvitee && !isValidInviteEmail(competitor?.invitedEmail);
  const initialValues =
    isInvitee && competitor
      ? {
          name: competitor.name,
          invitedEmail: competitor.invitedEmail ?? '',
        }
      : undefined;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Text
          variant="body2Medium"
          color={corner === 'A' ? 'error-default' : 'blue-default'}
          className={styles.label}
        >
          {`COMPETITOR ${corner}`}
        </Text>
        {!competitor && (
          <Button
            variant="secondary"
            subVariant="outlined"
            size="small"
            icon="userPlus"
            className={styles.inviteButton}
            onClick={handleOpenInviteModal}
          >
            Invite
          </Button>
        )}
      </div>

      <div className={styles.participant}>
        <Text variant="body2Medium" color="lights-medium">
          Participant
        </Text>
        {competitor ? (
          <div
            className={cn(styles.filled, {
              [styles.filledError]: hasInvalidEmail,
            })}
          >
            <button
              type="button"
              className={cn(styles.filledLeft, styles.editButton)}
              aria-label={`Edit participant ${competitor.name}`}
              disabled={!isInvitee}
              onClick={handleOpenInviteModal}
            >
              <div className={styles.avatarWrapper}>
                <img
                  src={competitor.avatarURL || '/favicon.png'}
                  alt=""
                  className={styles.avatar}
                />
                {!isRegistered && (
                  <span className={styles.pendingBadge}>
                    <img
                      src="/images/create-stream-wizard/help-circle.svg"
                      alt="Invite pending"
                      className={styles.pendingBadgeIcon}
                    />
                  </span>
                )}
              </div>
              <div className={styles.filledText}>
                <Text variant="body2Medium" className={styles.ellipsis}>
                  {competitor.name}
                </Text>
                {competitor.invitedEmail && (
                  <Text
                    variant="captionRegular"
                    color={hasInvalidEmail ? 'error-default' : 'lights-medium'}
                    className={styles.ellipsis}
                  >
                    {competitor.invitedEmail}
                  </Text>
                )}
              </div>
            </button>
            <button
              type="button"
              className={styles.removeButton}
              aria-label="Remove competitor"
              onClick={handleRemoveCompetitor}
            >
              <MillionsIcon name="close" size={14} />
            </button>
          </div>
        ) : (
          <CompetitorSearchInput
            onSelect={handleSearchSelect}
            excludeStoreIds={excludeStoreIds}
            initialSearchTerm={searchTerm}
            onSearchTermChange={onSearchTermChange}
            unpickedError={UNPICKED_ERROR}
          />
        )}
      </div>

      <InviteParticipantModal
        open={showInviteModal}
        onClose={handleCloseInviteModal}
        onInvite={handleInvite}
        initialValues={initialValues}
      />
    </div>
  );
};

export default CompetitorSlot;
